import { Link } from "react-router-dom";
import { Phone, MessageCircle, ChevronLeft } from "lucide-react";
import SEO, { localBusinessJsonLd } from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import FAQAccordion from "@/components/FAQAccordion";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import { PHONE_TEL, openWa } from "@/lib/wa";

export default function Services() {
  const { t, pick } = useLang();
  const { services, faqs } = useData();

  return (
    <main>
      <SEO title="خدمات ميرو المتنقلة في الرياض | تغيير إطارات، بنشر، حساسات، ترصيص، بطاريات" description="تعرف على خدمات ميرو المتنقلة في الرياض: تغيير إطارات السيارات، إصلاح البنشر، برمجة حساسات الكفرات، ترصيص الإطارات، وتغيير البطاريات — اطلب عبر واتساب 0507359999" path="/services" jsonLd={localBusinessJsonLd()} />
      <PageHero eyebrow="MERO SERVICES" title={t("home.servicesTitle")} sub={t("home.servicesSub")} />

      <section className="pb-20 sm:pb-28">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 md:grid-cols-2 lg:px-8">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={i % 2}>
              <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-coal/60 sm:flex-row" data-testid={`services-page-card-${s.slug}`}>
                <div className="h-52 sm:h-auto sm:w-2/5 shrink-0 overflow-hidden">
                  <img src={s.image} alt={pick(s.title_ar, s.title_en)} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h2 className="text-xl font-bold text-white group-hover:text-gold">{pick(s.title_ar, s.title_en)}</h2>
                  <p className="mt-3 flex-1 text-sm leading-8 text-mist">{pick(s.short_ar, s.short_en)}</p>
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    <Link to={s.slug === "tpms" ? "/tpms" : s.has_page ? `/services/${s.slug}` : s.slug === "tire-size-help" ? "/tires" : "/contact"} className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-ink transition-all hover:bg-gold-hover" data-testid={`service-detail-link-${s.slug}`}>
                      {t("cta.explore")}
                    </Link>
                    <button onClick={() => openWa(pick(s.wa_template_ar, s.wa_template_en))} className="rounded-full border border-[#1FAF5A]/50 px-5 py-2.5 text-sm font-bold text-[#2ecc71] transition-all hover:bg-[#1FAF5A]/10" data-testid={`service-wa-${s.slug}`}>
                      <MessageCircle size={14} className="inline" /> {t("common.whatsapp")}
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 bg-coal/40 py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <SectionHeading center eyebrow={{ ar: "الأسئلة الشائعة", en: "FAQ" }} title={{ ar: t("home.faqTitle"), en: t("home.faqTitle") }} sub={{ ar: t("home.faqSub"), en: t("home.faqSub") }} />
          <FAQAccordion items={(faqs || []).slice(0, 6).map((f) => ({ q: pick(f.q_ar, f.q_en), a: pick(f.a_ar, f.a_en) }))} />
        </div>
      </section>

      <section className="border-t border-white/5 py-14">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Reveal>
            <h2 className="mb-5 text-center text-lg font-bold text-white">خدمات متنقلة في أحياء الرياض</h2>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[
                  ["tire-change-riyadh", "تغيير كفرات متنقل"],
                  ["puncture-riyadh", "إصلاح بنشر متنقل"],
                  ["balancing-riyadh", "ترصيص كفرات متنقل"],
                  ["battery-riyadh", "تغيير بطاريات متنقل"],
                  ["tpms-riyadh", "برمجة حساسات الكفرات"],
                ].map(([to, label]) => (
                  <Link key={to} to={`/${to}`} className="rounded-full border border-white/12 bg-coal/60 px-4 py-2 text-sm text-zinc-300 transition-all hover:border-gold/50 hover:text-gold" data-testid={`services-popular-${to}`}>
                    {label}
                  </Link>
                ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
